import React, { useState, useEffect } from "react";
import {
  Space,
  Input,
  Select,
  Button,
  Tabs,
  Typography,
  Card,
  Badge,
  Switch,
  Form,
  Table,
  message,
  AutoComplete,
  Upload,
  Spin,
  Segmented,
  Radio,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  CodeOutlined,
  SettingOutlined,
  SwapOutlined,
  UploadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import { TRequest } from "./index";
import axios, { AxiosResponse } from "axios";
import useFetch from "../../../hooks/useFetch";
import CryptoJS from "crypto-js";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

interface RequestExecutorProps {
  request: TRequest;
  onSave: (req: TRequest) => void;
  collectionVariables?: { key: string; value: string }[];
}

const COMMON_HEADERS = [
  { value: "Content-Type" },
  { value: "Authorization" },
  { value: "Accept" },
  { value: "User-Agent" },
];

const DEFAULT_SYSTEM_HEADERS = [
  {
    key: "Postman-Token",
    value: "<calculated when request is sent>",
    enabled: true,
    isSystem: true,
  },
  {
    key: "Content-Type",
    value: "application/json",
    enabled: true,
    isSystem: true,
  },
  {
    key: "Content-Length",
    value: "<calculated when request is sent>",
    enabled: true,
    isSystem: true,
  },
  {
    key: "Host",
    value: "<calculated when request is sent>",
    enabled: true,
    isSystem: true,
  },
  {
    key: "User-Agent",
    value: "PostmanRuntime/7.49.1",
    enabled: true,
    isSystem: true,
  },
  { key: "Accept", value: "*/*", enabled: true, isSystem: true },
  {
    key: "Accept-Encoding",
    value: "gzip, deflate, br",
    enabled: true,
    isSystem: true,
  },
  { key: "Connection", value: "keep-alive", enabled: true, isSystem: true },
];

const mergeSystemHeaders = (currentHeaders: any[]) => {
  const merged = [...currentHeaders];
  DEFAULT_SYSTEM_HEADERS.forEach((sys) => {
    if (!merged.some((h) => h.key.toLowerCase() === sys.key.toLowerCase())) {
      merged.push(sys);
    }
  });
  return merged;
};

export const RequestExecutor: React.FC<RequestExecutorProps> = ({
  request,
  onSave,
  collectionVariables,
}) => {
  const { post } = useFetch();
  const [name, setName] = useState(request.name);
  const [method, setMethod] = useState(request.requestJson?.method || "GET");
  const [url, setUrl] = useState(request.requestJson?.url || "");
  const [isServerSide, setIsServerSide] = useState<boolean>(() => {
    const saved = localStorage.getItem("apiIsServerSide");
    if (saved !== null) return saved === "true";
    return request.requestJson?.isServerSide || false;
  });
  const [headers, setHeaders] = useState([
    ...mergeSystemHeaders(request.requestJson?.headers || []),
  ]);
  const [showSystemHeaders, setShowSystemHeaders] = useState(false);
  const [queryParams, setQueryParams] = useState([
    ...(request.requestJson?.query_params || []),
  ]);
  const [bodyContent, setBodyContent] = useState(
    request.requestJson?.body?.content || "",
  );
  const [formDataParams, setFormDataParams] = useState<
    Array<{
      key: string;
      type: "text" | "file";
      value: string;
      file?: File | null;
    }>
  >(
    (request.requestJson?.body?.form_data || []).map((item) => ({
      ...item,
      file: null,
    })),
  );
  const [bodyType, setBodyType] = useState(
    request.requestJson?.body?.type || "raw",
  );
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("1");
  const [activeResponseTab, setActiveResponseTab] = useState("1");

  const [fontSize, setFontSize] = useState<number>(() =>
    Number(localStorage.getItem("apiFontSize") || 14),
  );
  const [responseHeight, setResponseHeight] = useState(300);
  const [isDragging, setIsDragging] = useState(false);
  const [description, setDescription] = useState("");
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<{
    data: any;
    headers: any;
    status: number;
    statusText: string;
    time: number;
    size: number;
  } | null>(null);

  useEffect(() => {
    setName(request.name);
    setMethod(request.requestJson?.method || "GET");
    setUrl(request.requestJson?.url || "");
    setHeaders([...mergeSystemHeaders(request.requestJson?.headers || [])]);
    setQueryParams([...(request.requestJson?.query_params || [])]);
    const originalContent = request.requestJson?.body?.content || "";
    try {
      if (originalContent && request.requestJson?.body?.type === "raw") {
        setBodyContent(JSON.stringify(JSON.parse(originalContent), null, 2));
      } else {
        setBodyContent(originalContent);
      }
    } catch (e) {
      setBodyContent(originalContent);
    }
    setFormDataParams(
      (request.requestJson?.body?.form_data || []).map((item) => ({
        ...item,
        file: null,
      })),
    );
    setBodyType(request.requestJson?.body?.type || "raw");
    const savedServerSide = localStorage.getItem("apiIsServerSide");
    setIsServerSide(
      savedServerSide !== null
        ? savedServerSide === "true"
        : request.requestJson?.isServerSide || false,
    );
    setDescription(request.description || "");

    if (request.responseJson) {
      setResponse({
        data:
          request.responseJson.body ||
          request.responseJson.data ||
          request.responseJson,
        headers: request.responseJson.headers || {},
        status: request.responseJson.status || 200,
        statusText: request.responseJson.statusText || "Fetched from history",
        time: request.responseJson.time || 0,
        size: request.responseJson.size || 0,
      });
    } else {
      setResponse(null);
    }
    setJsonError(null);
  }, [request]);

  useEffect(() => {
    localStorage.setItem("apiFontSize", String(fontSize));
  }, [fontSize]);

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e: MouseEvent) => {
        setResponseHeight((prev) => {
          const newH = prev - e.movementY;
          return Math.max(100, Math.min(newH, 1000));
        });
      };
      const handleMouseUp = () => setIsDragging(false);
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging]);

  useEffect(() => {
    const baseUrlVar = collectionVariables?.find((v) =>
      ["base_url", "{{base_url}}", "baseUrl", "{{baseUrl}}"].includes(v.key),
    );

    if (baseUrlVar && baseUrlVar.value) {
      try {
        let hostValue = baseUrlVar.value;
        if (hostValue.includes("://")) {
          hostValue = new URL(hostValue).hostname;
        }

        setHeaders((prev) =>
          prev.map((h) => {
            if (h.key === "Host" && h.isSystem) {
              return { ...h, value: hostValue };
            }
            return h;
          }),
        );
      } catch (e) {
        // ignore invalid url
      }
    }
  }, [collectionVariables]);

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setBodyContent(val);
    if (bodyType === "raw" && val) {
      try {
        JSON.parse(val);
        setJsonError(null);
      } catch (err: any) {
        setJsonError(err.message);
      }
    } else {
      setJsonError(null);
    }
  };

  const interpolateUrl = (str: string) => {
    if (!str) return str;
    const interpolated = str.replace(/{{(.*?)}}/g, (match, key) => {
      const trimmedKey = key.trim();
      const variable = collectionVariables?.find(
        (v) => v.key === trimmedKey || v.key === `{{${trimmedKey}}}`,
      );
      return variable ? variable.value : match;
    });

    // Clean up double protocols that can occur if both the URL and variable include them
    return interpolated.replace(/(https?:\/\/)(https?:\/\/)/g, "$1");
  };

  const handleBeautifyRequest = () => {
    try {
      if (bodyContent) {
        const pretty = JSON.stringify(JSON.parse(bodyContent), null, 2);
        setBodyContent(pretty);
        setJsonError(null);
      }
    } catch (e) {
      setJsonError("Invalid JSON structure");
    }
  };

  const handleSend = async () => {
    if (jsonError && bodyType === "raw") {
      message.error("Cannot send request with invalid JSON body");
      return;
    }
    setLoading(true);
    setResponse(null);
    const startTime = Date.now();
    try {
      // Build final URL with params
      let finalUrl = interpolateUrl(url);

      // Auto-prefix http if missing for basic fetch logic
      if (finalUrl && !finalUrl.startsWith("http")) {
        finalUrl = `https://${finalUrl}`;
      }

      if (queryParams.length > 0 && finalUrl) {
        try {
          const urlObj = new URL(finalUrl);
          queryParams.forEach((p) => {
            if (p.key)
              urlObj.searchParams.append(
                interpolateUrl(p.key),
                interpolateUrl(p.value),
              );
          });
          finalUrl = urlObj?.toString();
        } catch (e) {
          // ignore invalid url
        }
      }

      // Build Headers
      const reqHeaders: Record<string, string> = {};
      headers
        .filter((h) => h.enabled && !h.value?.includes("<calculated"))
        .forEach((h) => {
          if (h.key)
            reqHeaders[interpolateUrl(h.key)] = interpolateUrl(h.value);
        });

      // Build Body
      let reqData: any = undefined;
      const finalHeaders = { ...reqHeaders };

      if (["POST", "PUT", "PATCH"].includes(method)) {
        if (bodyType === "raw" && bodyContent) {
          try {
            reqData = JSON.parse(bodyContent);
            if (!finalHeaders["Content-Type"])
              finalHeaders["Content-Type"] = "application/json";
          } catch (e) {
            reqData = bodyContent; // fallback to raw string
            if (!finalHeaders["Content-Type"])
              finalHeaders["Content-Type"] = "text/plain";
          }
        } else if (bodyType === "form-data" && formDataParams.length > 0) {
          reqData = new FormData();
          formDataParams.forEach((p) => {
            if (p.key) {
              if (p.type === "file" && p.file) {
                reqData.append(interpolateUrl(p.key), p.file);
              } else {
                reqData.append(interpolateUrl(p.key), interpolateUrl(p.value));
              }
            }
          });
          // Let axios set Content-Type for boundary automatically
          delete finalHeaders["Content-Type"];
        } else if (
          bodyType === "x-www-form-urlencoded" &&
          formDataParams.length > 0
        ) {
          reqData = new URLSearchParams();
          formDataParams.forEach((p) => {
            if (p.key && p.type === "text") {
              reqData.append(interpolateUrl(p.key), interpolateUrl(p.value));
            }
          });
          if (!finalHeaders["Content-Type"])
            finalHeaders["Content-Type"] = "application/x-www-form-urlencoded";
        }
      }

      if (isServerSide) {
        // Prepare payload for /forwardRequest
        const passkey = process.env.REACT_APP_PUBLIC_ENCRYPT_KEY || "";
        let encryptedData = "";

        if (reqData) {
          const dataToEncrypt =
            typeof reqData === "object"
              ? JSON.stringify(reqData)
              : String(reqData);
          encryptedData = CryptoJS.AES.encrypt(
            dataToEncrypt,
            passkey,
          ).toString();
        }

        const forwardPayload = {
          data: {
            url: finalUrl,
            method: method,
            headers: finalHeaders,
            params: queryParams.length > 0 ? JSON.stringify(queryParams) : "",
            data: reqData,
          },
        };

        const res = await post("/forwardRequest", forwardPayload);

        const endTime = Date.now();
        // Extract based on the sample response structure: { status, headers, data, statusText }
        const resData = res?.data || res;

        // If the response from server has the special structure, extract from it
        const actualData = resData?.data !== undefined ? resData.data : resData;
        const actualHeaders = resData?.headers || res?.headers || {};
        const actualStatus = resData?.status || res?.status || 200;
        const actualStatusText = resData?.statusText || res?.statusText || "OK";

        const dataStr =
          typeof actualData === "object"
            ? JSON.stringify(actualData)
            : String(actualData || "");

        setResponse({
          data: actualData,
          headers: actualHeaders,
          status: actualStatus,
          statusText: actualStatusText,
          time: endTime - startTime,
          size: new Blob([dataStr]).size,
        });
      } else {
        const res = await axios({
          method: method,
          url: finalUrl,
          headers: finalHeaders,
          data: reqData,
        });

        const endTime = Date.now();
        const dataStr =
          typeof res.data === "object"
            ? JSON.stringify(res.data)
            : String(res.data);

        setResponse({
          data: res.data,
          headers: res.headers,
          status: res.status,
          statusText: res.statusText,
          time: endTime - startTime,
          size: new Blob([dataStr]).size,
        });
      }
    } catch (error: any) {
      const endTime = Date.now();
      if (error.response) {
        const dataStr =
          typeof error.response.data === "object"
            ? JSON.stringify(error.response.data)
            : String(error.response.data);
        setResponse({
          data: error.response.data,
          headers: error.response.headers,
          status: error.response.status,
          statusText: error.response.statusText || "Error",
          time: endTime - startTime,
          size: new Blob([dataStr]).size,
        });
      } else {
        setResponse({
          data: error.message || "Network Error",
          headers: {},
          status: 0,
          statusText: "CORS or Network Error",
          time: endTime - startTime,
          size: 0,
        });
        message.warning(
          "Request failed. Ensure the URL is valid and CORS is enabled.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    // Map method back to eRequestType
    const mapMethodToType = (m: string) => {
      switch (m) {
        case "POST":
          return 1;
        case "GET":
          return 2;
        case "PUT":
          return 3;
        case "DELETE":
          return 4;
        case "PATCH":
          return 5;
        default:
          return 2;
      }
    };

    // Construct responseJson snippet based on the current response if available
    let responseJsonObj = undefined;
    if (response) {
      responseJsonObj = {
        status: response.status,
        body: response.data,
      };
    }

    onSave({
      ...request,
      name,
      description,
      requestType: mapMethodToType(method) as any,
      requestJson: {
        ...request.requestJson,
        method: method as any,
        url,
        headers,
        query_params: queryParams,
        body: {
          type: bodyType as any,
          content: bodyContent,
          language: bodyType === "raw" ? "json" : undefined,
          form_data: formDataParams.map((p) => ({
            key: p.key,
            type: p.type,
            value: p.value,
          })),
        },
        isServerSide,
      },
      responseJson: responseJsonObj || request.responseJson,
    });
  };

  const handleBeautify = () => {
    if (response?.data) {
      try {
        const pretty = JSON.stringify(
          typeof response.data === "string"
            ? JSON.parse(response.data)
            : response.data,
          null,
          2,
        );
        // We just update the display, not the actual state, but we can re-set it stringified
        setResponse({
          ...response,
          data:
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data,
        });
      } catch (e) {
        message.error("Cannot beautify non-JSON content");
      }
    }
  };

  const columns = (data: any[], setter: (val: any) => void) => [
    {
      title: "Key",
      dataIndex: "key",
      render: (text: string, record: any, index: number) => (
        <Input
          value={text}
          onChange={(e) => {
            const newData = [...data];
            newData[index].key = e.target.value;
            setter(newData);
          }}
          placeholder="Key"
        />
      ),
    },
    {
      title: "Value",
      dataIndex: "value",
      render: (text: string, record: any, index: number) => (
        <Input
          value={text}
          onChange={(e) => {
            const newData = [...data];
            newData[index].value = e.target.value;
            setter(newData);
          }}
          placeholder="Value"
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 60,
      render: (_: any, __: any, index: number) => (
        <Button
          danger
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => {
            const newData = [...data];
            newData.splice(index, 1);
            setter(newData);
          }}
        />
      ),
    },
  ];

  const headerColumns = [
    {
      title: "",
      key: "enabled",
      width: 40,
      render: (_: any, record: any) => (
        <input
          type="checkbox"
          checked={record.enabled !== false}
          onChange={(e) => {
            const newData = [...headers];
            const realIndex = headers.findIndex((h) => h === record);
            if (realIndex !== -1) {
              newData[realIndex].enabled = e.target.checked;
              setHeaders(newData);
            }
          }}
        />
      ),
    },
    {
      title: "Key",
      dataIndex: "key",
      render: (text: string, record: any) => (
        <AutoComplete
          options={COMMON_HEADERS}
          style={{ width: "100%" }}
          value={text}
          onChange={(val) => {
            const newData = [...headers];
            const realIndex = headers.findIndex((h) => h === record);
            if (realIndex !== -1) {
              newData[realIndex].key = val;
              setHeaders(newData);
            }
          }}
          placeholder="Key (e.g. Content-Type)"
          filterOption={(inputValue, option) =>
            option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
          }
          disabled={record.isSystem}
        />
      ),
    },
    {
      title: "Value",
      dataIndex: "value",
      render: (text: string, record: any) => (
        <Input
          value={text}
          onChange={(e) => {
            const newData = [...headers];
            const realIndex = headers.findIndex((h) => h === record);
            if (realIndex !== -1) {
              newData[realIndex].value = e.target.value;
              setHeaders(newData);
            }
          }}
          placeholder="Value"
          disabled={record.isSystem}
          style={record.isSystem ? { color: "#999", fontStyle: "italic" } : {}}
          suffix={
            record.isSystem &&
            text?.includes("<calculated") && (
              <Badge status="processing" title="Calculated automatically" />
            )
          }
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 60,
      render: (_: any, record: any) =>
        !record.isSystem && (
          <Button
            danger
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => {
              const newData = [...headers];
              const realIndex = headers.findIndex((h) => h === record);
              if (realIndex !== -1) {
                newData.splice(realIndex, 1);
                setHeaders(newData);
              }
            }}
          />
        ),
    },
  ];

  const formDataColumns = [
    {
      title: "Key",
      dataIndex: "key",
      render: (text: string, record: any, index: number) => (
        <Input
          value={text}
          onChange={(e) => {
            const newData = [...formDataParams];
            newData[index].key = e.target.value;
            setFormDataParams(newData);
          }}
          placeholder="Key"
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 100,
      render: (type: "text" | "file", record: any, index: number) => (
        <Select
          value={type}
          onChange={(val) => {
            const newData = [...formDataParams];
            newData[index].type = val;
            if (val === "file") newData[index].value = "";
            setFormDataParams(newData);
          }}
          style={{ width: 80 }}
        >
          <Option value="text">Text</Option>
          {bodyType === "form-data" && <Option value="file">File</Option>}
        </Select>
      ),
    },
    {
      title: "Value",
      dataIndex: "value",
      render: (text: string, record: any, index: number) =>
        record.type === "file" ? (
          <Space>
            <Upload
              beforeUpload={(file) => {
                const newData = [...formDataParams];
                newData[index].file = file;
                newData[index].value = file.name;
                setFormDataParams(newData);
                return false;
              }}
              showUploadList={false}
              fileList={record.file ? [record.file as any] : []}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
            {record.file && <Text type="secondary">{record.file.name}</Text>}
          </Space>
        ) : (
          <Input
            value={text}
            onChange={(e) => {
              const newData = [...formDataParams];
              newData[index].value = e.target.value;
              setFormDataParams(newData);
            }}
            placeholder="Value"
          />
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 60,
      render: (_: any, __: any, index: number) => (
        <Button
          danger
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => {
            const newData = [...formDataParams];
            newData.splice(index, 1);
            setFormDataParams(newData);
          }}
        />
      ),
    },
  ];

  return (
    <Card
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "none",
      }}
      bodyStyle={{
        padding: "0px",
        flex: 1,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ padding: "8px 24px", borderBottom: "1px solid #f0f0f0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isDescriptionOpen ? (
            <CaretDownOutlined
              onClick={() => setIsDescriptionOpen(false)}
              style={{ color: "#888", cursor: "pointer", fontSize: "12px" }}
            />
          ) : (
            <CaretRightOutlined
              onClick={() => setIsDescriptionOpen(true)}
              style={{ color: "#888", cursor: "pointer", fontSize: "12px" }}
            />
          )}
          <Input
            bordered={false}
            style={{
              fontSize: 16,
              fontWeight: 600,
              padding: 0,
              flex: 1,
            }}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        {isDescriptionOpen && (
          <div style={{ marginTop: 8, paddingLeft: 24 }}>
            <TextArea
              placeholder="Add request description..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                fontSize: 12,
                color: "#666",
                background: "#f9f9f9",
                border: "1px solid #eee",
                borderRadius: "4px",
              }}
            />
          </div>
        )}
      </div>

      <div
        style={{
          padding: "16px 24px",
          display: "flex",
          gap: 12,
          alignItems: "center",
        }}
      >
        <Space.Compact style={{ flex: 1 }}>
          <Select
            value={method}
            onChange={setMethod}
            style={{ width: 120 }}
            size="large"
          >
            <Option value="GET">GET</Option>
            <Option value="POST">POST</Option>
            <Option value="PUT">PUT</Option>
            <Option value="DELETE">DELETE</Option>
            <Option value="PATCH">PATCH</Option>
          </Select>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Enter request URL"
            style={{ flex: 1 }}
            size="large"
          />
        </Space.Compact>
        <Radio.Group
          size="small"
          value={isServerSide}
          onChange={(e) => {
            const val = e.target.value;
            setIsServerSide(val);
            localStorage.setItem("apiIsServerSide", String(val));
          }}
          buttonStyle="solid"
          style={{ marginLeft: 8, marginRight: 8 }}
        >
          <Radio.Button value={false}>Local</Radio.Button>
          <Radio.Button value={true}>Server</Radio.Button>
        </Radio.Group>
        <Button
          type="primary"
          onClick={handleSend}
          loading={loading}
          size="large"
        >
          Send
        </Button>
        <Button onClick={handleSave} size="large">
          Save
        </Button>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 24px" }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="small"
          tabBarExtraContent={
            activeTab === "3" ? (
              <Space>
                <Select
                  value={bodyType}
                  onChange={setBodyType}
                  size="small"
                  style={{ width: 180 }}
                >
                  <Option value="raw">raw (JSON)</Option>
                  <Option value="form-data">form-data</Option>
                  <Option value="x-www-form-urlencoded">
                    x-www-form-urlencoded
                  </Option>
                </Select>
                {bodyType === "raw" && (
                  <Space size={8} style={{ alignItems: "center" }}>
                    <ZoomOutOutlined
                      style={{ cursor: "pointer", fontSize: 16 }}
                      onClick={() => setFontSize(Math.max(10, fontSize - 2))}
                    />
                    <ZoomInOutlined
                      style={{ cursor: "pointer", fontSize: 16 }}
                      onClick={() => setFontSize(Math.min(30, fontSize + 2))}
                    />
                    <Button
                      type="text"
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(bodyContent);
                        message.success("Request copied!");
                      }}
                    >
                      Copy
                    </Button>
                    <Button
                      type="text"
                      size="small"
                      onClick={handleBeautifyRequest}
                    >
                      Beautify
                    </Button>
                  </Space>
                )}
              </Space>
            ) : null
          }
          items={[
            {
              key: "1",
              label: "Params",
              children: (
                <>
                  <Table
                    dataSource={queryParams}
                    columns={columns(queryParams, setQueryParams)}
                    pagination={false}
                    size="small"
                    rowKey={(_, i) => String(i)}
                  />
                  <Button
                    type="dashed"
                    style={{ marginTop: 8 }}
                    onClick={() =>
                      setQueryParams([...queryParams, { key: "", value: "" }])
                    }
                  >
                    <PlusOutlined /> Add Param
                  </Button>
                </>
              ),
            },
            {
              key: "2",
              label: (
                <Space>
                  Headers
                  <Badge
                    count={headers.filter((h) => h.enabled).length}
                    size="small"
                    style={{ backgroundColor: "#52c41a" }}
                  />
                </Space>
              ),
              children: (
                <>
                  <div
                    style={{
                      marginBottom: 8,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {showSystemHeaders
                        ? "Showing all headers"
                        : "System headers are hidden"}
                    </Text>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setShowSystemHeaders(!showSystemHeaders)}
                    >
                      {showSystemHeaders
                        ? "Hide hidden headers"
                        : `Show hidden headers (${DEFAULT_SYSTEM_HEADERS.length})`}
                    </Button>
                  </div>
                  <Table
                    dataSource={headers.filter(
                      (h) => showSystemHeaders || !h.isSystem,
                    )}
                    columns={headerColumns}
                    pagination={false}
                    size="small"
                    rowKey={(record, i) => (record.key || "key") + i}
                  />
                  <Button
                    type="dashed"
                    style={{ marginTop: 8 }}
                    onClick={() =>
                      setHeaders([
                        ...headers,
                        { key: "", value: "", enabled: true },
                      ])
                    }
                  >
                    <PlusOutlined /> Add Header
                  </Button>
                </>
              ),
            },
            {
              key: "3",
              label: "Body",
              children: (
                <div style={{ padding: "0 8px" }}>
                  {bodyType === "raw" ? (
                    <div style={{ position: "relative" }}>
                      <TextArea
                        rows={16}
                        value={bodyContent}
                        onChange={handleBodyChange}
                        style={{
                          fontFamily: "monospace",
                          fontSize: `${fontSize}px`,
                          lineHeight: 1.5,
                          borderColor: jsonError ? "red" : undefined,
                        }}
                      />
                      {jsonError && (
                        <div
                          style={{ color: "red", marginTop: 4, fontSize: 12 }}
                        >
                          Invalid JSON: {jsonError}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <Table
                        dataSource={formDataParams}
                        columns={formDataColumns}
                        pagination={false}
                        size="small"
                        rowKey={(_, i) => String(i)}
                      />
                      <Button
                        type="dashed"
                        style={{ marginTop: 8 }}
                        onClick={() =>
                          setFormDataParams([
                            ...formDataParams,
                            { key: "", type: "text", value: "" },
                          ])
                        }
                      >
                        <PlusOutlined /> Add Row
                      </Button>
                    </>
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>

      <div
        onMouseDown={() => setIsDragging(true)}
        style={{
          height: 6,
          cursor: "row-resize",
          background: isDragging ? "#1677ff" : "#f0f0f0",
          transition: "background 0.2s",
          margin: "0 24px",
          borderRadius: 3,
        }}
      />

      <div
        style={{
          height: responseHeight,
          borderTop: "1px solid #f0f0f0",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "8px 16px",
            background: "#fafafa",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text strong>Response</Text>
          {response && (
            <Space size="large">
              <Text>
                Status:{" "}
                <Badge
                  status={
                    response.status >= 200 && response.status < 300
                      ? "success"
                      : "error"
                  }
                  text={`${response.status} ${response.statusText}`}
                />
              </Text>
              <Text>
                Time: <span style={{ color: "green" }}>{response.time} ms</span>
              </Text>
              <Text>
                Size: <span style={{ color: "blue" }}>{response.size} B</span>
              </Text>
            </Space>
          )}
        </div>
        <div
          style={{ flex: 1, padding: 16, overflow: "auto", background: "#fff" }}
        >
          {!response && !loading && (
            <Text
              type="secondary"
              style={{ display: "block", textAlign: "center", marginTop: 40 }}
            >
              Hit Send to get a response
            </Text>
          )}
          {loading && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                paddingTop: 60,
              }}
            >
              <Spin size="large" />
              <div
                style={{
                  marginTop: 16,
                  color: "#888",
                  fontWeight: 500,
                  fontSize: 16,
                }}
              >
                Sending Request...
              </div>
            </div>
          )}
          {response && !loading && (
            <Tabs
              activeKey={activeResponseTab}
              onChange={setActiveResponseTab}
              size="small"
              tabBarExtraContent={
                activeResponseTab === "1" ? (
                  <Space size={2}>
                    <Button
                      type="text"
                      size="small"
                      onClick={() => {
                        const dataStr =
                          typeof response?.data === "object"
                            ? JSON.stringify(response.data, null, 2)
                            : String(response?.data || "");
                        navigator.clipboard.writeText(dataStr);
                        message.success("Response copied!");
                      }}
                    >
                      Copy
                    </Button>
                    <Button type="text" size="small" onClick={handleBeautify}>
                      Beautify
                    </Button>
                  </Space>
                ) : null
              }
              items={[
                {
                  key: "1",
                  label: "Body",
                  children: (
                    <div style={{ position: "relative" }}>
                      <pre
                        style={{
                          margin: 0,
                          padding: 12,
                          background: "#f5f5f5",
                          borderRadius: 6,
                          overflow: "auto",
                          minHeight: 300,
                          fontSize: `${fontSize}px`,
                          fontFamily: "monospace",
                          lineHeight: 1.5,
                        }}
                      >
                        {(() => {
                          const data = response.data;
                          if (typeof data === "object") {
                            return JSON.stringify(data, null, 2);
                          }
                          try {
                            return JSON.stringify(JSON.parse(data), null, 2);
                          } catch (e) {
                            return String(data);
                          }
                        })()}
                      </pre>
                    </div>
                  ),
                },
                {
                  key: "2",
                  label: "Headers",
                  children: (
                    <div
                      style={{
                        background: "#f5f5f5",
                        padding: 16,
                        borderRadius: 6,
                      }}
                    >
                      {Object.entries(response.headers).map(([k, v]) => (
                        <div key={k} style={{ marginBottom: 4 }}>
                          <strong>{k}:</strong> {String(v)}
                        </div>
                      ))}
                    </div>
                  ),
                },
              ]}
            />
          )}
        </div>
      </div>
    </Card>
  );
};
